/**
 * EGMMS V3 - Mold Service
 * 금형 관리 서비스 (V2 + V3 확장)
 */

import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { Mold } from '../types';
import { QRService } from './qr.service';
import { customerService } from './customer.service';
import { locationService } from './location.service';
import { formatMoldInfo } from '../utils/info-file.utils';
import { createSafeFolderName, getMoldFolderStructure } from '../utils/nas-path.utils';
import unifiedStorageService from './storage/unified-storage.service';
// ⭐ V3: 프로젝트 서비스 import
import { removeMoldFromProject } from './project.service';

const COLLECTION_NAME = 'molds';

// 헬퍼: Mold 배열에 거래처명/보관장소명 채우기
const populateMoldNames = async (molds: Mold[]): Promise<Mold[]> => {
  const [customers, locations] = await Promise.all([
    customerService.getAll(),
    locationService.getAll()
  ]);

  return molds.map(mold => ({
    ...mold,
    customerName: customers.find(c => c.id === mold.customerId)?.name || '알 수 없음',
    location: locations.find(l => l.id === mold.locationId)?.name || '알 수 없음'
  }));
};

/**
 * 금형 등록 (V3: projectId 처리 추가)
 */
export const createMold = async (
  moldData: Omit<Mold, 'id' | 'qrCode' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    const now = Timestamp.now();
    
    // customerId로 고객사명 조회
    const customer = await customerService.getById(moldData.customerId);
    const customerName = customer?.name || '알 수 없음';
    
    // locationId로 보관장소명 조회
    const location = await locationService.getById(moldData.locationId);
    const locationName = location?.name || '알 수 없음';
    
    // ⭐ V3: projectId 처리
    const dataToSave = {
      ...moldData,
      customerName,
      location: locationName,
      qrCode: '', // 임시로 빈 문자열
      createdAt: now,
      updatedAt: now
    };
    
    // 먼저 QR 없이 DB에 저장하여 실제 ID 얻기
    const docRef = await addDoc(collection(db, COLLECTION_NAME), dataToSave);
    
    console.log('🔍 생성된 실제 금형 ID:', docRef.id);
    
    // 실제 ID로 QR 코드 생성
    const realMold = {
      id: docRef.id,
      moldNumber: moldData.moldNumber
    };
    
    const qrData = QRService.generateQRData(realMold);
    const qrCode = QRService.createQRString(qrData);
    
    console.log('🔍 생성된 QR 데이터:', qrData);
    console.log('🔍 생성된 QR 문자열:', qrCode);
    
    // QR 코드를 실제 데이터로 업데이트
    await updateDoc(doc(db, COLLECTION_NAME, docRef.id), {
      qrCode,
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ QR 코드 업데이트 완료');
    
    // ⭐ INFO 파일 생성 (비동기 - 실패해도 금형 생성은 성공)
    try {
      const mold: Mold = {
        id: docRef.id,
        moldNumber: moldData.moldNumber,
        ...dataToSave,
        qrCode,
        createdAt: now,
        updatedAt: now,
      };
      
      // Info 파일 내용 생성
      const infoContent = formatMoldInfo(mold);
      
      // 폴더명 생성 (금형번호_금형명)
      const moldName = moldData.moldName || '';
      const folderName = moldName
        ? `${createSafeFolderName(moldData.moldNumber)}_${createSafeFolderName(moldName)}`
        : createSafeFolderName(moldData.moldNumber);
      
      // 1️⃣ 금형 폴더 구조 생성
      const folderPaths = getMoldFolderStructure(docRef.id, moldData.moldNumber, moldName);
      await unifiedStorageService.createFolderStructure(folderPaths);
      console.log('✅ 금형 폴더 구조 생성 완료:', folderPaths.length, '개');
      
      // 2️⃣ Info 파일명 및 경로 (Firebase 형식)
      const infoFilename = `${folderName}_mold_info.txt`;
      const infoPath = `molds/${folderName}/${infoFilename}`;
      
      // 3️⃣ Info 파일 업로드
      await unifiedStorageService.uploadTextFile(infoPath, infoContent, infoFilename);
      
      console.log('✅ 금형 Info 파일 생성 완료:', infoPath);
    } catch (infoError) {
      console.error('⚠️ 금형 Info 파일 생성 실패 (금형은 정상 생성됨):', infoError);
    }
    
    // ⭐ V3: projectId가 있으면 프로젝트에 금형 추가
    // (여기서는 프로젝트 서비스의 addMoldToProject를 사용하지 않고
    //  금형만 생성, 프로젝트 연결은 별도로 처리)
    
    return docRef.id;
  } catch (error) {
    console.error('❌ 금형 생성 실패:', error);
    throw error;
  }
};

// 모든 금형 조회
export const getMolds = async (): Promise<Mold[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const molds = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Mold));

    return await populateMoldNames(molds);
  } catch (error) {
    console.error('Error getting molds:', error);
    throw error;
  }
};

// 거래처별 금형 조회
export const getMoldsByCustomerId = async (customerId: string): Promise<Mold[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('customerId', '==', customerId)
    );
    const querySnapshot = await getDocs(q);
    
    const molds = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Mold));

    // 클라이언트 측에서 정렬
    const sorted = molds.sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.seconds || 0;
      return bTime - aTime; // 최신순
    });

    return await populateMoldNames(sorted);
  } catch (error) {
    console.error('Error getting molds by customerId:', error);
    throw error;
  }
};

// 금형번호 중복 체크
export const checkDuplicateMoldNumber = async (
  moldNumber: string, 
  excludeId?: string
): Promise<boolean> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('moldNumber', '==', moldNumber)
    );
    const querySnapshot = await getDocs(q);
    
    // excludeId가 있으면 해당 ID는 제외 (수정 시 자기 자신 제외)
    if (excludeId) {
      return querySnapshot.docs.some(doc => doc.id !== excludeId);
    }
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking duplicate mold number:', error);
    throw error;
  }
};

// 금형 검색 (번호 또는 이름으로)
export const searchMolds = async (searchTerm: string): Promise<Mold[]> => {
  try {
    const q1 = query(
      collection(db, COLLECTION_NAME), 
      where('moldNumber', '>=', searchTerm),
      where('moldNumber', '<=', searchTerm + '\uf8ff')
    );
    
    const q2 = query(
      collection(db, COLLECTION_NAME),
      where('moldName', '>=', searchTerm),
      where('moldName', '<=', searchTerm + '\uf8ff')
    );
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    const results = new Map();
    
    snapshot1.docs.forEach(doc => {
      results.set(doc.id, { id: doc.id, ...doc.data() } as Mold);
    });
    
    snapshot2.docs.forEach(doc => {
      results.set(doc.id, { id: doc.id, ...doc.data() } as Mold);
    });
    
    const molds = Array.from(results.values());
    return await populateMoldNames(molds);
  } catch (error) {
    console.error('Error searching molds:', error);
    throw error;
  }
};

/**
 * 금형 수정 (V3: projectId 변경 처리 추가)
 */
export const updateMold = async (id: string, moldData: Partial<Mold>): Promise<void> => {
  try {
    const moldRef = doc(db, COLLECTION_NAME, id);
    
    // customerId가 변경되면 customerName도 업데이트
    let updateData = { ...moldData };
    if (moldData.customerId) {
      const customer = await customerService.getById(moldData.customerId);
      updateData.customerName = customer?.name || '알 수 없음';
    }
    
    // locationId가 변경되면 location도 업데이트
    if (moldData.locationId) {
      const location = await locationService.getById(moldData.locationId);
      updateData.location = location?.name || '알 수 없음';
    }
    
    // ⭐ V3: projectId 변경 처리
    // (projectId가 변경되면 이전 프로젝트에서 제거하고 새 프로젝트에 추가)
    // 주의: 이 부분은 프로젝트 서비스의 addMoldToProject/removeMoldFromProject를 
    // 사용하는 것이 더 안전하므로, 여기서는 단순히 projectId만 업데이트
    
    await updateDoc(moldRef, {
      ...updateData,
      updatedAt: Timestamp.now()
    });
    
    // ⭐ INFO 파일 업데이트 (비동기 - 실패해도 금형 수정은 성공)
    try {
      // 업데이트된 금형 데이터 조회
      const updatedMold = await getMoldById(id);
      if (updatedMold) {
        // Info 파일 내용 생성
        const infoContent = formatMoldInfo(updatedMold);
        
        // 폴더명 생성 (금형번호_금형명)
        const moldName = updatedMold.moldName || '';
        const folderName = moldName
          ? `${createSafeFolderName(updatedMold.moldNumber)}_${createSafeFolderName(moldName)}`
          : createSafeFolderName(updatedMold.moldNumber);
        
        // Info 파일명 및 경로 (Firebase 형식)
        const infoFilename = `${folderName}_mold_info.txt`;
        const infoPath = `molds/${folderName}/${infoFilename}`;
        
        // Info 파일 업로드 (덮어쓰기)
        await unifiedStorageService.uploadTextFile(infoPath, infoContent, infoFilename);
        
        console.log('✅ 금형 Info 파일 업데이트 완료:', infoPath);
      }
    } catch (infoError) {
      console.error('⚠️ 금형 Info 파일 업데이트 실패 (금형은 정상 수정됨):', infoError);
    }
  } catch (error) {
    console.error('Error updating mold:', error);
    throw error;
  }
};

/**
 * 금형 삭제 (V3: 프로젝트 연결 해제 추가)
 */
export const deleteMold = async (id: string): Promise<void> => {
  try {
    // ⭐ V3: 프로젝트 연결 확인 및 해제
    const mold = await getMoldById(id);
    if (mold && mold.projectId) {
      // 프로젝트에서 금형 제거
      await removeMoldFromProject(mold.projectId, id);
    }
    
    // 금형 삭제
    const moldRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(moldRef);
  } catch (error) {
    console.error('Error deleting mold:', error);
    throw error;
  }
};

// 모든 금형의 customerName 일괄 업데이트 (마이그레이션용)
export const updateAllCustomerNames = async (): Promise<{ success: number; failed: number }> => {
  try {
    console.log('🔄 모든 금형의 customerName 업데이트 시작...');
    
    const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
    const customers = await customerService.getAll();
    
    let success = 0;
    let failed = 0;
    
    for (const moldDoc of querySnapshot.docs) {
      try {
        const moldData = moldDoc.data();
        const customer = customers.find(c => c.id === moldData.customerId);
        
        if (customer) {
          await updateDoc(doc(db, COLLECTION_NAME, moldDoc.id), {
            customerName: customer.name,
            updatedAt: Timestamp.now()
          });
          console.log(`✅ ${moldDoc.id}: ${customer.name}`);
          success++;
        } else {
          console.log(`⚠️ ${moldDoc.id}: 고객사 없음 (customerId: ${moldData.customerId})`);
          failed++;
        }
      } catch (error) {
        console.error(`❌ ${moldDoc.id} 업데이트 실패:`, error);
        failed++;
      }
    }
    
    console.log(`✅ 완료: 성공 ${success}, 실패 ${failed}`);
    return { success, failed };
  } catch (error) {
    console.error('Error updating all customer names:', error);
    throw error;
  }
};

// QR 코드로 금형 찾기
export const getMoldByQRCode = async (qrCode: string): Promise<Mold | null> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('qrCode', '==', qrCode)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const docSnapshot = querySnapshot.docs[0];
    const mold = { id: docSnapshot.id, ...docSnapshot.data() } as Mold;
    const populated = await populateMoldNames([mold]);
    return populated[0];
  } catch (error) {
    console.error('Error getting mold by QR code:', error);
    throw error;
  }
};

// ID로 금형 조회
export const getMoldById = async (id: string): Promise<Mold | null> => {
  try {
    const moldRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(moldRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const mold = { id: docSnap.id, ...docSnap.data() } as Mold;
    const populated = await populateMoldNames([mold]);
    return populated[0];
  } catch (error) {
    console.error('Error getting mold by ID:', error);
    throw error;
  }
};

// 금형번호로 금형 조회
export const getMoldByNumber = async (moldNumber: string): Promise<Mold | null> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('moldNumber', '==', moldNumber)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const docSnapshot = querySnapshot.docs[0];
    const mold = { id: docSnapshot.id, ...docSnapshot.data() } as Mold;
    const populated = await populateMoldNames([mold]);
    return populated[0];
  } catch (error) {
    console.error('Error getting mold by number:', error);
    throw error;
  }
};

/**
 * 프로젝트에 연결된 금형 목록 조회
 */
export const getMoldsByProjectId = async (projectId: string): Promise<Mold[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('projectId', '==', projectId),
      orderBy('moldNumber', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const molds = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Mold));

    return await populateMoldNames(molds);
  } catch (error) {
    console.error('프로젝트 금형 목록 조회 실패:', error);
    return [];
  }
};

// moldService 인스턴스 export
export const moldService = {
  createMold,
  getMolds,
  getMoldsByCustomerId,
  getMoldsByProjectId,
  searchMolds,
  updateMold,
  deleteMold,
  getMoldByQRCode,
  getMoldById,
  getMoldByNumber,
  updateAllCustomerNames,
  checkDuplicateMoldNumber
};
